import { Box, Column, Heading, Row, Txt } from "@stenajs-webui/core";
import { FlatButton, Label, stenaPlusCircle } from "@stenajs-webui/elements";
import { cssColor } from "@stenajs-webui/theme";
import { size, values } from "lodash";
import type { ReactNode } from "react";
import { useMediaQuery } from "react-responsive";

import { MAX_LENGTH_GOODS_DESCRIPTION } from "../../common/fieldLengths";
import type { GoodsItem, GoodsItems } from "../../common/goods/goodsUtils";
import { breakpoints } from "../../themes/defaultTheme";
import { DetailsFieldCell } from "../details/DetailsFieldCell";
import { Arrow } from "./Arrow";

const COLLAPSED_GOODS_LIMIT = 10;

interface BookingGoodsSectionProps {
  disabled?: boolean;
  goodsItems: GoodsItems;
  onAddGoodsItem: () => void;
  onShowAll?: () => void;
  renderGoodsDescription: (goodsItem: GoodsItem, id?: string) => ReactNode;
  renderGoodsType: (goodsItem: GoodsItem, id?: string) => ReactNode;
  renderGoodsWeight: (goodsItem: GoodsItem, id?: string) => ReactNode;
  responsive?: boolean;
  sectionHeaderRef?: React.Ref<HTMLDivElement>;
  showAll?: boolean;
  totalWeight: number;
}

export function BookingGoodsSection({
  goodsItems,
  onAddGoodsItem,
  totalWeight,
  disabled = false,
  sectionHeaderRef,
  renderGoodsType,
  renderGoodsDescription,
  renderGoodsWeight,
  responsive = true,
  showAll = false,
  onShowAll,
}: BookingGoodsSectionProps) {
  const narrowLayout =
    !useMediaQuery({ minWidth: breakpoints.md }) && responsive;

  const shouldShowGoods = size(goodsItems) > 0;

  const allGoodsItems = values(goodsItems);
  const canCollapse =
    onShowAll != null &&
    !showAll &&
    allGoodsItems.length > COLLAPSED_GOODS_LIMIT;
  const visibleGoodsItems = canCollapse
    ? allGoodsItems.slice(0, COLLAPSED_GOODS_LIMIT)
    : allGoodsItems;

  return (
    <Column gap={2} ref={sectionHeaderRef} flex={1}>
      <Heading variant={"h5"}>Goods (non-hazardous)</Heading>
      {shouldShowGoods &&
        (narrowLayout ? (
          visibleGoodsItems.map((goodsItem) => (
            <Column key={goodsItem.id} gap={2} className={"t_goods_row"}>
              <div className={"t_goods_type_cell"}>
                <DetailsFieldCell
                  flex={1}
                  label={"Goods type"}
                  renderField={(id) => renderGoodsType(goodsItem, id)}
                />
              </div>
              <Row
                gap={1}
                alignItems={"start"}
                className={"t_goods_description_cell"}
              >
                <Arrow />
                <DetailsFieldCell
                  flex={1}
                  label={"Goods description"}
                  maxCharacters={MAX_LENGTH_GOODS_DESCRIPTION}
                  renderField={(id) => renderGoodsDescription(goodsItem, id)}
                />
              </Row>
              <Row
                gap={1}
                alignItems={"start"}
                className={"t_goods_weight_cell"}
              >
                <Arrow />
                <DetailsFieldCell
                  flex={1}
                  label={"Weight (kg)"}
                  renderField={(id) => renderGoodsWeight(goodsItem, id)}
                />
              </Row>
            </Column>
          ))
        ) : (
          <table style={{ width: "100%", borderSpacing: "8px" }}>
            <thead>
              <tr>
                <th style={{ width: "20px", textAlign: "start" }}>
                  <Txt
                    size={"small"}
                    variant={"bold"}
                    color={cssColor("--lhds-color-ui-800")}
                  >
                    #
                  </Txt>
                </th>
                <th style={{ width: "100px", textAlign: "start" }}>
                  <Label text={"Goods type"} />
                </th>
                <th style={{ textAlign: "start", width: "340px" }}>
                  <Label
                    text={"Goods description"}
                    maxCharacters={MAX_LENGTH_GOODS_DESCRIPTION}
                  />
                </th>
                <th style={{ width: "150px", textAlign: "start" }}>
                  <Label text={"Weight (kg)"} />
                </th>
              </tr>
            </thead>
            <tbody>
              {visibleGoodsItems.map((goodsItem, index) => (
                <tr key={goodsItem.id} className={"t_goods_row"}>
                  <td style={{ verticalAlign: "top" }}>
                    <Box height={36} justifyContent={"center"}>
                      <Txt>{index + 1}</Txt>
                    </Box>
                  </td>
                  <td
                    style={{ verticalAlign: "top" }}
                    className={"t_goods_type_cell"}
                  >
                    {renderGoodsType(goodsItem)}
                  </td>
                  <td
                    style={{ verticalAlign: "top" }}
                    className={"t_goods_description_cell"}
                  >
                    {renderGoodsDescription(goodsItem)}
                  </td>
                  <td
                    style={{ verticalAlign: "top" }}
                    className={"t_goods_weight_cell"}
                  >
                    {renderGoodsWeight(goodsItem)}
                  </td>
                  <td></td>
                </tr>
              ))}
              {canCollapse && (
                <tr>
                  <td colSpan={4}>
                    <Row justifyContent={"center"}>
                      <FlatButton
                        className={"t_show_all_goods_button"}
                        label={`Show all (${allGoodsItems.length})`}
                        onClick={onShowAll}
                      />
                    </Row>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        ))}
      {canCollapse && narrowLayout && (
        <Row justifyContent={"center"}>
          <FlatButton
            className={"t_show_all_goods_button"}
            label={`Show all (${allGoodsItems.length})`}
            onClick={onShowAll}
          />
        </Row>
      )}
      <Row alignItems={"center"} justifyContent={"space-between"}>
        <FlatButton
          className={"t_add_goods_button"}
          leftIcon={stenaPlusCircle}
          label={"Add goods"}
          disabled={disabled}
          onClick={onAddGoodsItem}
        />
        <Txt variant={"bold"}>Cargo weight: {totalWeight} kg</Txt>
      </Row>
    </Column>
  );
}
